import { APPROVAL_RECORD_TYPES, type ApprovalRule } from '@felix/domain';
import {
  ActionIcon,
  Button,
  Checkbox,
  Menu,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Textarea,
} from '@mantine/core';
import { IconDots, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { roleNameOf } from './MatrixDiff.js';
import {
  EMPTY_RULE,
  formToRule,
  type RuleForm,
  ruleFormErrors,
  ruleToForm,
  type SubjectOption,
} from './matrix-model.js';
import { roleLabel } from './RolesScreen.js';
import classes from './Setup.module.css';
import type { MatrixRoleItem } from './setup-types.js';

export interface ApprovalRulesTableProps {
  rules: readonly ApprovalRule[];
  roles: readonly MatrixRoleItem[];
  editable: boolean;
  onAdd: () => void;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
  subjectLabel?: (subject: string) => string;
}

/**
 * Onay matrisi tablosu (ADR 0042 §24; mimari belge §7.1): kayıt türü, konu (yalnız ISM formu),
 * giren roller, en çok iki adım ve ofis işareti (ADR 0026 §3), tutar kademesi, süre ve öz onay
 * gerekçesi.
 */
export function ApprovalRulesTable({
  rules,
  roles,
  editable,
  onAdd,
  onEdit,
  onRemove,
  subjectLabel = (s) => s,
}: ApprovalRulesTableProps) {
  const { t } = useTranslation();
  const r = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.matrix.rules.${key}`, options);
  const step = (rule: ApprovalRule, index: number) => {
    const id = rule.steps[index];
    if (!id) return <span className={classes.muted}>{index === 0 ? r('noStep') : '—'}</span>;
    return (
      <span className={classes.chips}>
        {roleNameOf(t, roles, id)}
        {rule.officeSteps?.[index] ? <StatusBadge tone="info">{r('office')}</StatusBadge> : null}
      </span>
    );
  };
  return (
    <>
      {editable ? (
        <div className={classes.searchRow}>
          <p className={classes.stepText}>{r('help')}</p>
          <Button leftSection={<IconPlus size={16} />} onClick={onAdd}>
            {r('add')}
          </Button>
        </div>
      ) : null}
      {rules.length === 0 ? (
        <EmptyState title={r('empty')} />
      ) : (
        <section className={table.tableWrap} aria-label={r('table')}>
          <table className={`${table.table} ${classes.tallRows} ${classes.tableRules}`}>
            <thead>
              <tr>
                <th scope="col" className={classes.key}>
                  {r('columns.recordType')}
                </th>
                <th scope="col">{r('columns.subject')}</th>
                <th scope="col">{r('columns.enteredBy')}</th>
                <th scope="col">{r('columns.step1')}</th>
                <th scope="col">{r('columns.step2')}</th>
                <th scope="col">{r('columns.amount')}</th>
                <th scope="col" data-numeric>
                  {r('columns.deadline')}
                </th>
                <th scope="col">{r('columns.selfApproval')}</th>
                {editable ? (
                  <th scope="col" data-numeric>
                    {r('columns.actions')}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: kuralların kimliği yok; sıra kimliktir
                <tr key={index}>
                  <td className={classes.key}>
                    {t(`setup.matrix.recordTypes.${rule.recordType}`)}
                  </td>
                  <td>
                    {rule.recordType === 'ism_form'
                      ? rule.subject
                        ? subjectLabel(rule.subject)
                        : r('dialog.subjectNone')
                      : '—'}
                  </td>
                  <td>
                    {rule.enteredBy === 'any'
                      ? r('any')
                      : rule.enteredBy.map((id) => roleNameOf(t, roles, id)).join(', ')}
                  </td>
                  <td>{step(rule, 0)}</td>
                  <td>{step(rule, 1)}</td>
                  <td>
                    {[
                      rule.minAmount !== undefined ? r('from', { amount: rule.minAmount }) : null,
                      rule.maxAmount !== undefined ? r('to', { amount: rule.maxAmount }) : null,
                    ]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </td>
                  <td data-numeric>
                    {rule.deadlineHours !== undefined
                      ? r('hours', { count: rule.deadlineHours })
                      : '—'}
                  </td>
                  <td title={rule.selfApprovalReason}>{rule.selfApprovalReason ?? '—'}</td>
                  {editable ? (
                    <td data-numeric>
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={r('actionsOf', { index: index + 1 })}
                          >
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item onClick={() => onEdit(index)}>{r('edit')}</Menu.Item>
                          <Menu.Item color="red" onClick={() => onRemove(index)}>
                            {r('remove')}
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

/** Onay kuralı penceresi: konu yalnız ISM formunda; iki adım alanı (ADR 0026 §3). */
export function ApprovalRuleDialog({
  rule,
  roles,
  subjects,
  onClose,
  onSave,
}: {
  rule: ApprovalRule | null;
  roles: readonly MatrixRoleItem[];
  subjects: SubjectOption[];
  onClose: () => void;
  onSave: (rule: ApprovalRule) => void;
}) {
  const { t } = useTranslation();
  const d = (key: string) => t(`setup.matrix.rules.dialog.${key}`);
  const [form, setForm] = useState<RuleForm>(rule ? ruleToForm(rule) : EMPTY_RULE);
  const errors = ruleFormErrors(form);
  const error = (field: keyof RuleForm) =>
    errors[field]
      ? t(`setup.errors.${errors[field]}`, { defaultValue: t('setup.errors.GENERIC') })
      : undefined;
  const set = (change: Partial<RuleForm>) => setForm((current) => ({ ...current, ...change }));
  const roleOptions = roles.map((role) => ({ value: role.id, label: roleLabel(t, role) }));
  const stepOptions = [{ value: '', label: d('noStep') }, ...roleOptions];
  // ISM formunda ofis adımı kurulamaz (sahip kararı 2026-10-17; ADR 0073): işaret yalnız eski
  // kuralda görünür ki kaldırılabilsin
  const officeShown = (checked: boolean) => form.recordType !== 'ism_form' || checked;
  return (
    <Modal
      opened
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={rule ? d('editTitle') : d('newTitle')}
      size="lg"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (Object.keys(errors).length > 0) return;
          onSave(formToRule(form));
        }}
      >
        <Select
          label={d('recordType')}
          data={APPROVAL_RECORD_TYPES.map((type) => ({
            value: type,
            label: t(`setup.matrix.recordTypes.${type}`),
          }))}
          value={form.recordType}
          allowDeselect={false}
          onChange={(value) =>
            set({
              recordType: value ?? 'ism_form',
              ...(value !== 'ism_form' ? { subject: null } : {}),
            })
          }
          mb="sm"
        />
        {form.recordType === 'ism_form' ? (
          <Select
            label={d('subject')}
            data={[{ value: '', label: d('subjectNone') }, ...subjects]}
            value={form.subject ?? ''}
            allowDeselect={false}
            searchable
            onChange={(value) => set({ subject: value || null })}
            error={error('subject')}
            mb="sm"
          />
        ) : null}
        <Checkbox
          label={d('anyRole')}
          checked={form.enteredBy === 'any'}
          onChange={(event) => set({ enteredBy: event.currentTarget.checked ? 'any' : [] })}
          mb="xs"
        />
        {form.enteredBy !== 'any' ? (
          <MultiSelect
            label={d('enteredBy')}
            data={roleOptions}
            value={form.enteredBy}
            onChange={(value) => set({ enteredBy: value })}
            error={error('enteredBy')}
            searchable
            mb="sm"
          />
        ) : null}
        <div className={classes.pair}>
          <Select
            label={d('step1')}
            data={stepOptions}
            value={form.step1 ?? ''}
            allowDeselect={false}
            onChange={(value) =>
              set({ step1: value || null, ...(value ? {} : { step1Office: false }) })
            }
            error={error('step1')}
          />
          {officeShown(form.step1Office) ? (
            <Checkbox
              label={d('office')}
              checked={form.step1Office}
              disabled={!form.step1}
              onChange={(event) => set({ step1Office: event.currentTarget.checked })}
              error={error('step1Office')}
              mb={8}
            />
          ) : null}
        </div>
        <div className={classes.pair}>
          <Select
            label={d('step2')}
            data={stepOptions}
            value={form.step2 ?? ''}
            allowDeselect={false}
            disabled={!form.step1}
            onChange={(value) =>
              set({ step2: value || null, ...(value ? {} : { step2Office: false }) })
            }
          />
          {officeShown(form.step2Office) ? (
            <Checkbox
              label={d('office')}
              checked={form.step2Office}
              disabled={!form.step2}
              onChange={(event) => set({ step2Office: event.currentTarget.checked })}
              error={error('step2Office')}
              mb={8}
            />
          ) : null}
        </div>
        <div className={classes.triple}>
          <NumberInput
            label={d('minAmount')}
            min={0}
            value={form.minAmount}
            onChange={(value) => set({ minAmount: value === '' ? '' : String(value) })}
            error={error('minAmount')}
          />
          <NumberInput
            label={d('maxAmount')}
            min={0}
            value={form.maxAmount}
            onChange={(value) => set({ maxAmount: value === '' ? '' : String(value) })}
            error={error('maxAmount')}
          />
          <NumberInput
            label={d('deadline')}
            min={1}
            allowDecimal={false}
            value={form.deadlineHours}
            onChange={(value) => set({ deadlineHours: value === '' ? '' : String(value) })}
            error={error('deadlineHours')}
          />
        </div>
        <Textarea
          label={d('selfApproval')}
          description={d('selfApprovalHelp')}
          value={form.selfApprovalReason}
          maxLength={1000}
          autosize
          minRows={2}
          onChange={(event) => set({ selfApprovalReason: event.currentTarget.value })}
          mt="sm"
        />
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {d('cancel')}
          </Button>
          <Button type="submit" disabled={Object.keys(errors).length > 0}>
            {d('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
